import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlParamReplicaId, urlProjectMetadata } from '../../Constants';
import { IAction, SlicePropsWithData } from '../store';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectMetadataState = {
    data: null,
    loading: false,
    error: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    resetProjectMetadata(state: IProjectMetadataState) {
        Object.assign(state, initialState);
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches the available document views for a given project database.
     * @param replicaId - ReplicaId for the target project database.
     * @returns Promise for the HTTP request.
     */
    getProjectMetadata: async (replicaId: string): Promise<ProjectMetadataDictionary> => {
        try {
            const url = urlProjectMetadata.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectMetadataState>) => {
        /**
         * Reducers for the 'getProjectMetadata' thunk.
         */
        builder.addCase(getProjectMetadata.pending, (state: Draft<IProjectMetadataState>) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            getProjectMetadata.fulfilled,
            (state: Draft<IProjectMetadataState>, action: IAction<ProjectMetadataDictionary>) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            getProjectMetadata.rejected,
            (state: Draft<IProjectMetadataState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = null;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjectMetadata = createAsyncThunk('project/metadata', thunks.getProjectMetadata);

/** Dictionary that contains data on the configured views for a project database. */
export type ProjectMetadataDictionary = { [id: string]: IProjectViewData };

/**
 * Interface that describes the state object for this redux slice.
 */
export type IProjectMetadataState = SlicePropsWithData<ProjectMetadataDictionary>;

/**
 * Interface that describes data returned from the backend for a project database view.
 */
export interface IProjectViewData {
    /** Database icon. */
    Icon: string | null;

    /** Database title. */
    Title: string;

    /** Database extended config. */
    ExtendedConfig: string;
}

export interface IExtendedMetaDataConfig {
    /** Extended config icon. */
    icon: string;
}

/**
 * Create the redux slice.
 */
export const projectMetadataSlice = createSlice({
    name: 'projectMetadata',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export slice reducers.
 */
export const { resetProjectMetadata } = projectMetadataSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectMetadataSlice.reducer;
